import { emitCommand } from './commandBus';
import {
  clearDrafts,
  clearEmails,
  clearPulledDomains,
  clearScrapeSession,
  clearSeenEmails,
  hideDomain,
  hideEmail,
  saveCompanies,
  saveHiddenDomains,
  saveHiddenEmails,
  unhideDomain,
  unhideEmail,
} from './storage';

/**
 * Apply assistant/MCP actions to App setters + command bus.
 * @returns {string[]} human-readable summaries
 */
export function applyAssistantActions(actions, ctx) {
  if (!Array.isArray(actions) || !actions.length) return [];
  const notes = [];

  for (const action of actions) {
    if (!action || !action.type) continue;
    switch (action.type) {
      case 'navigate':
        if (action.view) {
          let view = action.view;
          if (view === 'drafts') view = 'emails';
          ctx.setView?.(view);
          notes.push(`view → ${view}`);
        }
        break;

      case 'set_email_filters':
        ctx.setEmailFilters?.((prev) => ({
          verifications:
            action.verifications != null ? action.verifications : prev.verifications,
          classifications:
            action.classifications != null
              ? action.classifications
              : prev.classifications,
        }));
        notes.push('email filters updated');
        break;

      case 'hide_domain':
        ctx.setHiddenDomains?.(hideDomain(action.domain));
        notes.push(`hid domain ${action.domain}`);
        break;

      case 'unhide_domain':
        ctx.setHiddenDomains?.(unhideDomain(action.domain));
        notes.push(`unhid domain ${action.domain}`);
        break;

      case 'hide_email':
        ctx.setHiddenEmails?.(hideEmail(action.email));
        notes.push(`hid email ${action.email}`);
        break;

      case 'unhide_email':
        ctx.setHiddenEmails?.(unhideEmail(action.email));
        notes.push(`unhid email ${action.email}`);
        break;

      case 'set_scrape_prefs':
        emitCommand('set_scrape_prefs', action.prefs || {});
        notes.push('scrape prefs updated');
        break;

      case 'set_draft_template':
        emitCommand('set_draft_template', {
          subject: action.subject,
          body: action.body,
        });
        notes.push('draft template updated');
        break;

      case 'set_pull_hypothesis':
        emitCommand('set_pull_hypothesis', action.pull || {});
        notes.push('PULL hypothesis updated');
        break;

      case 'start_scrape':
        ctx.setView?.('domains');
        emitCommand('start_scrape');
        notes.push('scrape started');
        break;

      case 'start_grab_emails':
        ctx.setView?.('contacts');
        emitCommand('start_grab_emails', { skipConfirm: true });
        notes.push('contact pull started');
        break;

      case 'stop_grab_emails':
        emitCommand('stop_grab_emails');
        notes.push('contact pull stopped');
        break;

      case 'stop_scrape':
        emitCommand('stop_scrape');
        notes.push('scrape stopped');
        break;

      case 'set_contact_append_mode':
        emitCommand('set_contact_append_mode', { append: Boolean(action.append) });
        notes.push(`contact append → ${Boolean(action.append)}`);
        break;

      case 'download_csv':
        if (action.target) {
          emitCommand('download_csv', { target: action.target });
          notes.push(`download ${action.target} CSV`);
        }
        break;

      case 'apply_draft_template':
      case 'write_emails':
        ctx.setView?.('emails');
        emitCommand('apply_draft_template');
        notes.push('write emails triggered');
        break;

      case 'clear_data':
        if (action.target === 'emails') {
          clearEmails();
          clearSeenEmails();
          clearPulledDomains();
          ctx.setEmails?.([]);
          notes.push('cleared emails');
        } else if (action.target === 'drafts') {
          clearDrafts();
          ctx.setDrafts?.([]);
          notes.push('cleared drafts');
        } else if (action.target === 'companies') {
          ctx.setCompanies?.(saveCompanies([]));
          notes.push('cleared companies');
        } else if (action.target === 'scrape_session') {
          ctx.setScrapeSession?.(clearScrapeSession());
          notes.push('cleared scrape session');
        }
        break;

      case 'set_hidden_domains':
        if (Array.isArray(action.domains)) {
          ctx.setHiddenDomains?.(saveHiddenDomains(action.domains));
          notes.push('replaced domain blacklist');
        }
        break;

      case 'set_hidden_emails':
        if (Array.isArray(action.emails)) {
          ctx.setHiddenEmails?.(saveHiddenEmails(action.emails));
          notes.push('replaced email blacklist');
        }
        break;

      default:
        notes.push(`unknown action ${action.type}`);
    }
  }

  return notes;
}
